import dayjs from 'dayjs'
import { useCallback } from 'react'

import { getReadHistoryList } from '@/api/api'
import type { ReadHistory } from '@/api/types/user'
import { HistoryCollapsible } from '@/components/shared/history-collapsible'
import { formatSecondsJa, formatTime, isDurationMime } from '@/lib/time-format'

type Props = {
  episodeId: number
}

/** 読書履歴のCollapsibleリスト */
export const ReadHistoryCollapsible = ({ episodeId }: Props) => {
  const fetchItems = useCallback(
    () => getReadHistoryList(100, undefined, episodeId).then((res) => res.items),
    [episodeId],
  )

  return (
    <HistoryCollapsible<ReadHistory>
      label="読書履歴を表示"
      emptyMessage="読書履歴がありません"
      fetchItems={fetchItems}
      getDateKey={(h) => dayjs(h.started_at).format('YYYY-MM-DD')}
      formatDateHeader={(dateKey) => dayjs(dateKey).format('YYYY/M/D (dd)')}
      getItemKey={(h) => h.id}
      renderItem={(h) => {
        const start = dayjs(h.started_at)
        const end = dayjs(h.ended_at)
        const durationSec = end.diff(start, 'second')
        const isDuration = isDurationMime(h.mime_type)
        const progressLabel = isDuration
          ? `${formatTime(h.progress_from)}→${formatTime(h.progress_to)}`
          : `p.${h.progress_from}→${h.progress_to}`
        return (
          <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md bg-muted/30">
            <span className="text-xs">
              {start.format('HH:mm')} → {end.format('HH:mm')}
              {durationSec >= 60 && (
                <span className="text-[11px] text-muted-foreground ml-1">({formatSecondsJa(durationSec)})</span>
              )}
            </span>
            <span className="text-[11px] text-muted-foreground ml-auto">{progressLabel}</span>
          </div>
        )
      }}
      renderFooter={(items) => {
        const totalSeconds = items.reduce((acc, h) => acc + dayjs(h.ended_at).diff(dayjs(h.started_at), 'second'), 0)
        return (
          <p className="text-xs text-muted-foreground text-center py-1">
            合計: {formatSecondsJa(totalSeconds)} / {items.length}セッション
          </p>
        )
      }}
    />
  )
}
